import { defineStore } from 'pinia';
import {
	useCreateCart,
	useAddUpdateCart,
	useGetCartQuery,
	useRemoveCartItem,
} from '~/data/cart-v2';
import { StatsigSingleton } from '@laam/statsig-client';
import { useQueryClient } from '@tanstack/vue-query';
import { useLoganStore } from './logan';
import { useCurrency } from './currency';
import type { LineItemObjectType } from '~/types/Checkout';
import { useCartStore } from './cart';
import { getSubTotalNew } from '../utils/checkout';
export const useCartStoreNew = defineStore('cartNew', () => {
	const { $eventClient } = useNuxtApp();
	const LoganStore = useLoganStore();
	const { loganId } = storeToRefs(LoganStore);
	const CurrencyStore = useCurrency();
	const { getFormattedPrice } = CurrencyStore;
	const { currencyCode } = storeToRefs(CurrencyStore);
	const cartHash = ref<string>('');
	const stableId = ref<string>('');
	const queryClient = useQueryClient();
	const loadingList = ref<number[]>([]);
	const cartErrorMsg = ref<{
		[key: number]: string;
	}>({});
	onMounted(() => {
		hydrate();
		stableId.value = StatsigSingleton.getStableID();
		if (localStorage.getItem('cartHash') && localStorage.getItem('cartId')) {
			localStorage.removeItem('cartId');
			getCartRefetch();
		}
	});
	const shouldMigrateCartItems = computed(() => {
		if (process.client) {
			const oldCartId = localStorage.getItem('cartId');
			if (oldCartId) {
				return true;
			}
		}
		return false;
	});

	const persist = () => {
		localStorage.setItem('cartHash', cartHash.value);
	};
	const hydrate = () => {
		const cart = localStorage.getItem('cartHash');
		if (cart) {
			cartHash.value = cart;
		}
	};

	const { mutateAsync: createCart } = useCreateCart();
	const { mutateAsync: addUpdateCartMutatation } = useAddUpdateCart();
	const { mutateAsync: removeCartItem } = useRemoveCartItem();
	const {
		data: cartData,
		isLoading: getCartLoading,
		refetch: getCartRefetch,
	} = useGetCartQuery(stableId, cartHash, loganId, shouldMigrateCartItems);
	const lineItems = computed(() => {
		if (cartData.value) {
			return cartData.value.line_items?.filter((item) => !item.out_of_stock);
		}
		return [];
	});
	const outOfStockLineItems = computed(() => {
		if (cartData.value) {
			return cartData.value.line_items?.filter(
				(item) => item.out_of_stock,
			) as LineItemObjectType[];
		}
		return [];
	});
	const subTotal = computed(() => {
		if (cartData.value) {
			return cartData.value.cost.subtotal;
		}
		return 0;
	});
	const cartQuantity = computed(() => {
		if (cartData.value) {
			return cartData.value.quantity;
		}
		return 0;
	});
	const cartId = computed(() => {
		if (cartData.value) {
			return cartData.value.id;
		}
		return undefined;
	});
	const nativeCartAmount = computed(() => {
		return getSubTotalNew(
			lineItems.value ?? [],
			LoganStore.isHvc,
			true,
			getFormattedPrice,
			currencyCode.value,
		);
	});

	const createCartNew = async (
		items: { id: number; quantity: number }[],
		oldCartId: string | null = null,
	) => {
		try {
			const payload = {
				stableId: stableId.value,
				payload: {
					line_items: items,
				},
				update: false,
				oldCartId: oldCartId,
			};
			const data = await createCart(payload);
			queryClient.setQueryData(['loganId', loganId], {
				line_items: data.line_items,
				cost: data.cost,
				quantity: data.quantity,
				hash: data.hash,
			});
			if (localStorage.getItem('cartId')) {
				localStorage.removeItem('cartId');
			}
			cartHash.value = data.hash;
			persist();
			if (oldCartId) {
				$eventClient.sendEvent('cart-migration-success', {
					oldCartId: oldCartId,
					itemsIDs: items.map((item) => item.id),
				});
			}
			return data;
		} catch (error) {
			console.error('Error creating cart:', error);
			if (oldCartId) {
				$eventClient.sendEvent('cart-migration-failure', {
					oldCartId: oldCartId,
					itemsIDs: items.map((item) => item.id),
				});
			}
			throw new Error('Failed to create cart');
		}
	};
	const setCartErrorMessage = (msg: string, timeout: number, id: number) => {
		cartErrorMsg.value[id] = msg;
		setTimeout(() => {
			cartErrorMsg.value[id] = '';
		}, timeout);
	};
	const updateCartValidations = (
		id: number,
		itemQty: number,
		update: boolean,
	) => {
		const item = lineItems.value?.find((item) => item.id === id) ?? null;
		if (update && item) {
			if (itemQty > 5) {
				setCartErrorMessage('Max buyable quantity reached.', 3000, id);
				return false;
			} else if (itemQty > item.variant_inventory) {
				setCartErrorMessage('Available quantity limit reached.', 3000, id);
				return false;
			}
		} else if (!update && item) {
			if (itemQty + item.quantity > 5) {
				setCartErrorMessage('Max buyable quantity reached.', 3000, id);
				return false;
			}
		}
		return true;
	};

	const addUpdateCart = async (
		items: { id: number; quantity: number }[],
		update: boolean,
	) => {
		try {
			if (!cartHash.value) {
				return await createCartNew(items);
			}
			const validItems = items.filter((item) => {
				const isValid = updateCartValidations(item.id, item.quantity, update);
				if (isValid && update) {
					loadingList.value.push(item.id);
				}
				return isValid;
			});

			if (validItems.length === 0) return;

			const data = await addUpdateCartMutatation({
				cartHash: cartHash.value,
				stableId: stableId.value,
				update: update,
				payload: {
					line_items: validItems,
				},
			});
			queryClient.setQueryData(['loganId', loganId], {
				line_items: data.line_items,
				cost: data.cost,
				quantity: data.quantity,
			});
			if (update) {
				loadingList.value = loadingList.value.filter(
					(id) => !validItems.some((item) => item.id === id),
				);
			}
			return data;
		} catch (error) {
			console.error('Error adding/updating cart:', error);
			throw new Error('Failed to add/update cart');
		}
	};
	const removeFromCart = async (ids: number[]) => {
		try {
			loadingList.value.push(...ids);
			const data = await removeCartItem({
				cartHash: cartHash.value,
				stableId: stableId.value,
				payload: {
					id: ids,
				},
			});
			loadingList.value = loadingList.value.filter(
				(item) => !ids.includes(item),
			);
			queryClient.setQueryData(['loganId', loganId], {
				line_items: data.line_items,
				cost: data.cost,
				quantity: data.quantity,
			});
		} catch (error) {
			console.error('Error removing item from cart:', error);
			throw new Error('Failed to remove item from cart');
		}
	};
	const handleCartSignout = () => {
		cartHash.value = '';
		persist();
	};
	// function to migrate old cart items to new cart
	const migrateCartItems = async () => {
		try {
			const CartStore = useCartStore();
			await CartStore.getCartById();
			const { lineItems } = storeToRefs(CartStore);
			if (!lineItems.value.length) {
				return;
			}
			const items = lineItems.value.map((item) => ({
				id: parseFloat(item.id.split('/')[4]!),
				quantity: item.quantity,
			}));

			const payload = items.map((item) => ({
				id: item.id,
				quantity: item.quantity,
			}));
			localStorage.removeItem('cartId');
			await getCartRefetch();
			if (cartHash.value) {
				await addUpdateCart(payload, false);
			} else {
				await createCartNew(payload, CartStore.cartId);
			}
		} catch (error) {
			console.error('Error migrating cart items:', error);
			throw new Error('Failed to migrate cart items');
		}
	};

	watch(cartData, (newVal) => {
		if (newVal && cartHash.value !== newVal.hash && newVal.hash) {
			cartHash.value = newVal.hash;
			persist();
		}
	});

	watch(shouldMigrateCartItems, async (newVal) => {
		if (newVal) {
			await migrateCartItems();
		}
	});

	return {
		cartHash,
		createCartNew,
		addUpdateCart,
		lineItems,
		subTotal,
		cartQuantity,
		getCartRefetch,
		nativeCartAmount,
		getCartLoading,
		outOfStockLineItems,
		loadingList,
		cartErrorMsg,
		removeFromCart,
		stableId,
		cartData,
		handleCartSignout,
		cartId,
	};
});

export const useCartStoreNewTest = defineStore('cartNew', () => {
	const outOfStockLineItems = ref([]);
	const lineItems = ref([
		{
			id: 41891673112660,
			brand: 'Baroque',
			price: 12513,
			weight: 1.9800000190734863,
			quantity: 7,
			hvc_price: 11277.18,
			product_id: 7620425449556,
			image_source:
				'https://cdn.shopify.com/s/files/1/2337/7003/files/cropped_image-174038773611086_67e160cf-28e0-4037-842f-f7a52089e2cc.jpg?v=1740545715',
			product_title: 'BQU-UF-D689-00',
			variant_title: 'Unstitched',
			out_of_stock: false,
			converted_price: 0,
			compare_at_price: 12900,
			variant_inventory: 11,
			options: [{ values: ['Unstitched'], name: 'Size', position: 1 }],
			selected_options: [{ name: 'Size', value: 'Unstitched' }],
			cod_status: null,
			product_handle: 'bqu-uf-d689-00',
			estimated_shipping_date: '2025-05-20T22:30:51.792960',
		},
	]);
	const cartQuantity = ref(5);
	const getCartLoading = ref(false);
	const loadingList = ref([]);
	const nativeCartAmount = ref(5000);
	const cartErrorMsg = ref<{
		[key: number]: string;
	}>({
		41891673112660: 'This item is out of stock.',
	});
	const cartHash = ref('41891673112660');
	return {
		outOfStockLineItems,
		lineItems,
		cartQuantity,
		getCartLoading,
		loadingList,
		nativeCartAmount,
		cartErrorMsg,
		cartHash,
	};
});
